/**
 * WebSocket Service
 * Handles WebSocket connection, message handling, and reconnection logic
 * Implements a connection pool to ensure only one connection per URL
 */

class WebSocketService {
	// Static connection pool to track active connections by URL
	static connections = {};

	/**
	 * Get an existing WebSocketService instance or create a new one
	 * @param {string} url - WebSocket URL
	 * @returns {WebSocketService} - WebSocketService instance
	 */
	static getInstance(url) {
		if (!WebSocketService.connections[url]) {
			WebSocketService.connections[url] = new WebSocketService(url);
		}
		return WebSocketService.connections[url];
	}

	constructor(url) {
		this.url = url;
		this.socket = null;
		this.isConnected = false;
		this.reconnectAttempts = 0;
		this.maxReconnectAttempts = 5; // Allow 5 reconnection attempts by default
		this.reconnectInterval = 3000; // 3 seconds
		this.listeners = {};
		this.messageQueue = [];
		this.connectionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	/**
	 * Connect to the WebSocket service
	 * @param {Object} authToken - Authentication token for secure connection
	 * @returns {Promise} - Resolves when connection is established
	 */
	connect(authToken) {
		return new Promise((resolve, reject) => {
			try {
				// If already connected with the same URL, just resolve
				if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
					resolve();
					return;
				}

				// Close existing connection if any
				if (this.socket) {
					this.socket.close();
				}

				// Create a new WebSocket connection with Authorization token in URL
				// Since WebSocket API doesn't support custom headers, we include the token in the URL
				let connectionUrl = this.url;

				// Append the token as a query parameter if provided
				if (authToken) {
					const separator = connectionUrl.includes('?') ? '&' : '?';
					connectionUrl = `${connectionUrl}${separator}token=${encodeURIComponent(authToken)}`;
				}

				// The server redirects HTTP to HTTPS, so we need to use WSS protocol for WebSockets over HTTPS
				// Replace ws:// with wss:// in the URL to ensure secure WebSocket connection
				if (connectionUrl.startsWith('ws://')) {
					connectionUrl = connectionUrl.replace('ws://', 'wss://');
				}

				// Ensure we're using wss:// protocol for secure connections
				if (connectionUrl.startsWith('http://')) {
					connectionUrl = connectionUrl.replace('http://', 'wss://');
				} else if (connectionUrl.startsWith('https://')) {
					connectionUrl = connectionUrl.replace('https://', 'wss://');
				}
				this.socket = new WebSocket(connectionUrl);

				this.socket.onopen = (event) => {
					this.isConnected = true;
					this.reconnectAttempts = 0;

					// Send any queued messages
					this._processQueue();

					resolve();
				};

				// Setup other event handlers

				this.socket.onclose = (event) => {
					this.isConnected = false;
					// Store the close code for reconnection logic
					this.socket.closeCode = event.code;
					this._handleReconnect();
					this._triggerEvent('close', event);
				};

				this.socket.onerror = (error) => {
					this._triggerEvent('error', error);
					reject(error);
				};

				this.socket.onmessage = (event) => {
					try {
						const data = JSON.parse(event.data);
						this._triggerEvent('message', data);

						// Handle specific message types
						if (data?.event) {
							this._triggerEvent(data.event, data);
						}
					} catch (error) {
						this._triggerEvent('error', {
							type: 'parse_error',
							originalEvent: event,
							error
						});
					}
				};
			} catch (error) {
				reject(error);
			}
		});
	}

	/**
	 * Disconnect from the WebSocket service
	 */
	disconnect() {
		if (this.socket) {
			this.socket.close(1000, 'Client disconnected');
			this.socket = null;
			this.isConnected = false;

			// Remove this instance from the connections pool
			if (WebSocketService.connections[this.url]) {
				delete WebSocketService.connections[this.url];
			}
		}
	}

	/**
	 * Send a message to the WebSocket service
	 * @param {Object} message - Message to send
	 * @returns {boolean} - True if sent, false if queued
	 */
	send(message) {
		if (!message) {
			return false;
		}

		const messageString = typeof message === 'string'
			? message
			: JSON.stringify(message);

		if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
			this.socket.send(messageString);
			return true;
		} else {
			// Queue the message to send when connection is established
			this.messageQueue.push(messageString);
			return false;
		}
	}

	/**
	 * Register an event listener
	 * @param {string} event - Event name
	 * @param {Function} callback - Callback function
	 */
	on(event, callback) {
		if (!this.listeners[event]) {
			this.listeners[event] = [];
		}
		this.listeners[event].push(callback);
	}

	/**
	 * Remove an event listener
	 * @param {string} event - Event name
	 * @param {Function} callback - Callback function to remove
	 */
	off(event, callback) {
		if (this.listeners[event]) {
			this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
		}
	}

	/**
	 * Process queued messages
	 * @private
	 */
	_processQueue() {
		if (this.messageQueue.length > 0 && this.isConnected) {
			while (this.messageQueue.length > 0) {
				const message = this.messageQueue.shift();
				this.socket.send(message);
			}
		}
	}

	/**
	 * Handle reconnection logic
	 * @private
	 */
	_handleReconnect() {
		// Only attempt to reconnect if the connection was closed unexpectedly
		// Code 1000 is normal closure (user initiated disconnect)
		if (this.socket && this.socket.closeCode === 1000) {
			return;
		}

		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			this.reconnectAttempts++;

			// Store the current URL to extract token if present
			const currentUrl = this.socket && this.socket.url;
			let authToken = null;

			// Extract token from the current URL if it exists
			if (currentUrl && currentUrl.includes('token=')) {
				const tokenMatch = currentUrl.match(/token=([^&]+)/);
				if (tokenMatch && tokenMatch[1]) {
					authToken = decodeURIComponent(tokenMatch[1]);
				}
			}

			// Use exponential backoff for reconnection attempts
			const delay = Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1), 30000);

			setTimeout(() => {
				this.connect(authToken).catch(error => {
					console.error('WS Reconnection attempt failed:');
				});
			}, delay);
		} else {
			this._triggerEvent('reconnect_failed');
		}
	}

	/**
	 * Trigger event callbacks
	 * @param {string} event - Event name
	 * @param {*} data - Event data
	 * @private
	 */
	_triggerEvent(event, data) {
		if (this.listeners[event]) {
			this.listeners[event].forEach(callback => {
				try {
					callback(data);
				} catch (error) {
					console.error(`Error in ${event} event handler:`, error);
				}
			});
		}
	}
}

export default WebSocketService;